import { Injectable, signal } from '@angular/core';

type CookiebotConsent = {
    preferences: boolean;
    statistics: boolean;
    marketing: boolean;
};

// Cookiebot attaches itself to the global window object with no bundled types
type CookiebotWindow = Window & { Cookiebot?: { consent?: Partial<CookiebotConsent> } };

export type ConsentCategory = keyof CookiebotConsent;

const COOKIEBOT_EVENTS = ['CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline'];

@Injectable()
export class ConsentService {
    private preferencesConsent = signal(false);
    private statisticsConsent = signal(false);
    private marketingConsent = signal(false);

    readonly preferences = this.preferencesConsent.asReadonly();
    readonly statistics = this.statisticsConsent.asReadonly();
    readonly marketing = this.marketingConsent.asReadonly();

    private loadedCategories = new Set<ConsentCategory>();

    constructor() {
        this.update();
        COOKIEBOT_EVENTS.forEach((eventName) => window.addEventListener(eventName, () => this.update()));
    }

    registerLoadedScript(...categories: ConsentCategory[]): void {
        categories.forEach((category) => this.loadedCategories.add(category));
    }

    private update(): void {
        const consent = (window as CookiebotWindow).Cookiebot?.consent;

        const withdrawn = (category: ConsentCategory, previous: boolean) =>
            previous && !consent?.[category] && this.loadedCategories.has(category);

        // Already loaded third-party scripts cannot be unloaded, so a reload is the only way to stop them
        if (
            withdrawn('preferences', this.preferencesConsent()) ||
            withdrawn('statistics', this.statisticsConsent()) ||
            withdrawn('marketing', this.marketingConsent())
        ) {
            window.location.reload();
            return;
        }

        this.preferencesConsent.set(!!consent?.preferences);
        this.statisticsConsent.set(!!consent?.statistics);
        this.marketingConsent.set(!!consent?.marketing);
    }
}
